import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { completionStatus } from "@codemirror/autocomplete";
import { useWindowSize } from "@react-hooks-library/core";
import ReactCodeMirror, {
  BasicSetupOptions,
  Compartment,
  EditorState,
  EditorView,
  Extension,
  ReactCodeMirrorProps,
  ReactCodeMirrorRef,
  StateField,
} from "@uiw/react-codemirror";
import { twJoin, twMerge } from "tailwind-merge";

import { showUpdatedSuggestionsPlugin } from "src/base-components/CodeInput/Autocomplete/useAutocompleteExtension";
import { useFocusHandlers } from "src/base-components/CodeInput/CodeInputFocusHandlers";
import {
  CodeInputTheme,
  CursorInvisibleTheme,
  TruncateLinesTheme,
} from "src/base-components/CodeInput/Themes";
import { useEditorBounds } from "src/base-components/CodeInput/useEditorBounds";
import { useDispatchCreateEvent } from "src/base-components/CodeInput/useOnCreateEvent";
import { useSetDataLoc } from "src/base-components/CodeInput/useSetDataLoc";
import { Tooltip } from "src/design-system/Tooltip";
import { HIGHLIGHT_CLASSES } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { toastError } from "src/utils/toastError";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

export type CodeInputProps = Pick<
  ReactCodeMirrorProps,
  "onBlur" | "readOnly" | "placeholder" | "children"
> & {
  /**
   * Use disabled to signal users that the input is not editable (forbidden cursor and grayed out)
   * If you just want to prevent the user from editing the input use readOnly
   */
  disabled?: boolean;
  bgColor?: "bg-green-50";
  onChange: (newVal: string) => void;
  value: string;
  error?: string | boolean;
  prefix?: "data." | "$" | "%";
  dataLoc?: string;
  variant?: "cell" | "cell-expandable";
  transparentBackground?: boolean;
  plaintext?: boolean;
  completionExtension?: Extension;
  containerDataLoc?: string;
  lazy?: boolean;
  isSearchHighlighted?: boolean;
};

const CodeMirrorDefaultSetup: BasicSetupOptions = {
  lineNumbers: false,
  highlightActiveLine: false,
  highlightActiveLineGutter: false,
  foldGutter: false,
  searchKeymap: false,
};

// https://discuss.codemirror.net/t/dynamically-change-linewrapping/3027
export const lineWrappingComp = new Compartment();
const DefaultExtensions: Extension[] = [
  // Prevents adding of newlines
  EditorState.transactionFilter.of((tr) => {
    return tr.newDoc.lines > 1 ? [] : [tr];
  }),
  // Prevents pasting of newlines (after trying to trim them)
  EditorView.domEventHandlers({
    paste: (event, view) => {
      event.preventDefault(); // Prevent default paste behavior

      const clipboardData = event.clipboardData;
      if (clipboardData) {
        const pastedText = clipboardData.getData("text/plain");
        // Remove only trailing newlines
        const sanitizedText = pastedText.trim();

        if (sanitizedText.split("\n").length > 1) {
          toastError("Multi-line pasting isn't supported");
        } else {
          view.dispatch({
            changes: {
              from: view.state.selection.main.from,
              to: view.state.selection.main.to,
              insert: sanitizedText,
            },
            selection: {
              anchor: view.state.selection.main.from + sanitizedText.length,
            },
          });
        }
      }
    },
  }),
  // Line wrapping by default only gets switched on and off when focusing expandable fields
  lineWrappingComp.of([]),
];

/**
 * This field is present to determine if the input is prefixed with "data.".
 */
export const isDataPrefixed = StateField.define({
  create: () => {
    return true;
  },
  update: (value) => value,
});

const minHeight = "min-h-[32px]";

type GetDynamicEditorWidthArgs = {
  // Due to a bug in Scorecard Node value is sometimes undefined.
  // The null check is a workaround to prevent that editor from crashing.
  value: string | undefined;
  windowWidth: number;
  editorDivLeft: number;
  preChildWidth: number;
  outerWrapperLeft: number;
  outerWrapperWidth: number;
};

const getDynamicEditorWidth = ({
  value,
  windowWidth,
  editorDivLeft,
  preChildWidth,
  outerWrapperLeft,
  outerWrapperWidth,
}: GetDynamicEditorWidthArgs): number => {
  const MIN_DISTANCE_TO_VIEWPORT_EDGE = 28;

  const pixelToViewportEdge = windowWidth - editorDivLeft;
  const widthLimitFromViewport =
    pixelToViewportEdge - MIN_DISTANCE_TO_VIEWPORT_EDGE;

  const CHARACTER_WIDTH = 7.2;
  const PADDING_IN_EDITOR = 30;
  const lengthOfCharacters =
    Math.floor((value?.length || 0) * CHARACTER_WIDTH) + PADDING_IN_EDITOR;

  const widthOfChidrenAndCharacters = lengthOfCharacters + preChildWidth;

  const widthCodeEditorCanFillWithinOuterCountainer =
    outerWrapperWidth - (editorDivLeft - outerWrapperLeft);

  return Math.max(
    Math.min(widthOfChidrenAndCharacters, widthLimitFromViewport),
    widthCodeEditorCanFillWithinOuterCountainer,
  );
};

const resolveBorderClassNames = (
  variant: CodeInputProps["variant"],
  hasPrefix: boolean,
  error?: string | boolean,
) => {
  if (variant === "cell-expandable") {
    return {
      wrapper: "",
      dormantInputInner: "",
      input: twJoin(
        "border border-transparent focus-within:border-indigo-600",
        "focus-within:shadow-codeInput",
        error && "border-red-600 ring-2 ring-red-400/25",
      ),
    };
  }

  if (variant === "cell") {
    return {
      wrapper: "",
      dormantInputInner: "",
      input: "",
    };
  }

  return {
    wrapper: hasPrefix ? "rounded-r-lg" : "rounded-lg",
    dormantInputInner: twJoin("rounded-r-lg", !hasPrefix && "rounded-l-lg"),
    input: twJoin(
      "rounded-r-lg",
      "border focus-within:border-indigo-600",
      "focus-within:shadow-codeInput",
      !hasPrefix && "rounded-l-lg",
    ),
  };
};

type Stage = "dormant" | "awake";
type DormantBufferedEvent =
  | React.FocusEvent<HTMLDivElement>
  | React.PointerEvent<HTMLDivElement>;

export const CodeInput = forwardRef<ReactCodeMirrorRef, CodeInputProps>(
  (
    {
      onChange,
      onBlur,
      value,
      disabled = false,
      error,
      prefix = false,
      placeholder,
      dataLoc,
      children,
      variant,
      transparentBackground = false,
      completionExtension,
      plaintext,
      bgColor,
      containerDataLoc,
      readOnly,
      lazy = false,
      isSearchHighlighted,
    },
    editorRef,
  ) => {
    const [outerWrapperRef] = useHighlightedScrollIntoView(isSearchHighlighted);
    const preDisplayedChildrenRef = useRef<HTMLDivElement>(null);

    const editor = useRef<ReactCodeMirrorRef>(null);
    useImperativeHandle(editorRef, () => editor.current as ReactCodeMirrorRef);
    const { onCreateEditor } = useSetDataLoc(dataLoc, editor.current);
    const { dispatchCreateEvent } = useDispatchCreateEvent();
    const { width: windowWidth } = useWindowSize();

    const absolute = variant !== "cell";
    const expandable = absolute;
    const dataPrefix = prefix === "data.";

    const { maxX } = useEditorBounds();

    // initial stage is dormant meaning we do not load code mirror
    const [stage, setStage] = useState<Stage>(lazy ? "dormant" : "awake");
    // while in dormant we buffer incoming events
    const dormantEventBuffer = useRef<DormantBufferedEvent[]>([]);
    // When we receve an interesting event we wake up, i.e. load code mirror
    const wake = (evt: DormantBufferedEvent) => {
      dormantEventBuffer.current.push(evt);
      setStage("awake");
    };

    const borderClassNames = resolveBorderClassNames(variant, !!prefix, error);

    // Once code mirror is loaded and we are aware re replay the buffered events
    useEffect(() => {
      if (
        stage === "awake" &&
        editor.current?.editor &&
        dormantEventBuffer.current.length > 0
      ) {
        const target = editor.current.editor!;
        dormantEventBuffer.current.forEach((event) => {
          if (event.type === "focus") {
            setTimeout(() => {
              const element = target.querySelector("[contenteditable=true]");
              if (element) {
                (element as HTMLElement).focus();
              }
            }, 0);
          } else {
            target.dispatchEvent(
              new Event(event.type, {
                ...event,
              }),
            );
          }
        });
        dormantEventBuffer.current.length = 0;
      }
    }, [stage, editor]);

    // Dynamically update cm-editor width for non cell inputs.
    // We rely on line wrapping to automatically adjust the height of the editor.
    // This stops the editor from growing along the x-axis which is why we manually adjust its width.
    useEffect(() => {
      if (stage !== "awake") return;
      const editorDiv = editor.current?.editor;
      const preChildrenWidth = preDisplayedChildrenRef.current?.clientWidth;
      const outerWrapperDiv = outerWrapperRef.current;
      if (
        expandable &&
        editorDiv &&
        preChildrenWidth !== undefined &&
        outerWrapperDiv
      ) {
        const newDynamicWidth = getDynamicEditorWidth({
          value,
          windowWidth: maxX === null ? windowWidth : maxX,
          editorDivLeft: editorDiv.getBoundingClientRect().left,
          preChildWidth: preChildrenWidth,
          outerWrapperLeft: outerWrapperDiv.getBoundingClientRect().left,
          outerWrapperWidth: outerWrapperDiv.clientWidth,
        });
        editorDiv.style.width = newDynamicWidth + "px";
      }
    }, [expandable, value, windowWidth, maxX, stage]);

    const extensions = useMemo(() => {
      const extensions = [...DefaultExtensions];
      if (variant === "cell") {
        extensions.push(EditorView.lineWrapping);
      }
      if (completionExtension) {
        extensions.push(completionExtension);
        extensions.push(showUpdatedSuggestionsPlugin);
        if (prefix === "data.") {
          extensions.push(isDataPrefixed);
        }
      }
      return extensions;
    }, [completionExtension, variant, prefix]);

    const { handleFocus, handleBlur } = useFocusHandlers({
      editor,
      disabled,
      toggleWrappingOnFocus: expandable,
      toggleScrollbarOnFocus: expandable,
      onBlur,
    });

    let textColor;
    if (plaintext) {
      textColor = "text-gray-800";
    } else if (disabled) {
      textColor = "text-placeholder";
    } else {
      textColor = dataPrefix ? "text-green-600" : "text-indigo-600";
    }

    let prefixTextColor = textColor;
    if (!disabled && !plaintext) {
      prefixTextColor = "text-indigo-600";
    }

    let bgColorClass;
    if (transparentBackground) {
      bgColorClass = "bg-transparent";
    } else if (disabled) {
      bgColorClass = "bg-gray-100";
    } else {
      bgColorClass = bgColor || "bg-white";
    }

    // One CSS style is defined in index.css because the Theme does not allow accessing the most outer element (cm-theme)
    const theme = useMemo(
      () =>
        EditorView.theme({
          ...CodeInputTheme,
          ...(expandable && TruncateLinesTheme),
          ...((disabled || readOnly) && CursorInvisibleTheme),
        }),
      [disabled, expandable, readOnly],
    );

    const hasErrorMessage = typeof error === "string";

    // We need to prevent the default here because otherwise a click on the children opens the completions on the input
    const wrappedChildren = (
      <div
        ref={preDisplayedChildrenRef}
        className="!h-full flex min-h-[30px] items-center self-start pt-0.5"
        onFocus={(e) => e.preventDefault()}
      >
        {children}
      </div>
    );

    const inner = (
      <Tooltip
        disabled={
          !(
            hasErrorMessage &&
            // We dont want to show the tooltip when the completion is open
            (!editor.current?.view?.state ||
              completionStatus(editor.current.view?.state) !== "active")
          )
        }
        placement="top"
        title={hasErrorMessage ? error : undefined}
        asChild
      >
        <div
          className={twJoin(
            "item-center flex h-full w-full",
            absolute && "absolute min-w-full",
          )}
        >
          {prefix && (
            <div
              className={twJoin(
                "flex items-center rounded-l-lg border-gray-200 border-y border-l py-1 pr-2 pl-3 font-code-12",
                minHeight,
                transparentBackground ? "bg-transparent" : "bg-gray-100",
                prefixTextColor,
              )}
              data-loc={`${dataLoc}-prefix`}
              style={{
                fontFamily: "monospace",
                fontWeight: 400,
              }}
            >
              {prefix}
            </div>
          )}
          <div
            className={twMerge(
              "min-w-0 grow",
              borderClassNames.wrapper,
              expandable && minHeight,
              expandable &&
                "focus-within:h-fit focus-within:w-fit focus-within:min-w-fit",
              bgColorClass,
              isSearchHighlighted && HIGHLIGHT_CLASSES,
            )}
            data-loc={`${dataLoc}-wrapper`}
          >
            {stage === "dormant" ? (
              <div
                className={twJoin(
                  "flex h-full min-h-0 w-full min-w-0 max-w-full font-inter-normal-12px",
                  expandable ? "focus-within:max-h-[300px]" : "items-center",
                  !prefix && "pl-2",
                  borderClassNames.input,
                  textColor,
                  error && "border-red-600 ring-2 ring-red-400/25",
                  disabled && "cursor-not-allowed",
                  !disabled && "cursor-text",
                )}
                data-loc={containerDataLoc}
                onFocus={wake}
                onPointerEnter={wake}
              >
                {wrappedChildren}
                <div
                  className={twJoin(
                    "flex h-full min-h-0 w-full min-w-0 items-center pr-0.5 pl-1.5 font-code-12",
                    borderClassNames.dormantInputInner,
                  )}
                  contentEditable={!disabled}
                  data-loc={dataLoc}
                  style={{
                    fontFamily: "monospace",
                    resize: "none", // Not needed for div, but keeping it for consistency
                  }}
                  suppressContentEditableWarning={true} // Suppresses React's warning about using contenteditable
                >
                  {/**
                   * Span needed because the parent div needs to be flex for layout, but text-ellipsis does not work with the flex there.
                   * This can be seen as the pendent to .cm-line
                   */}
                  <span
                    className={twJoin(
                      "block overflow-hidden text-ellipsis whitespace-nowrap",
                      value
                        ? ""
                        : "text-placeholder before:content-[attr(data-placeholder)]",
                    )}
                    data-placeholder={placeholder as string}
                  >
                    {value}
                  </span>
                </div>
              </div>
            ) : (
              <ReactCodeMirror
                ref={editor}
                basicSetup={CodeMirrorDefaultSetup}
                className={twJoin(
                  "flex h-full min-h-0 w-full min-w-0 max-w-full font-code-12",
                  absolute && "items-start",
                  expandable ? "focus-within:max-h-[300px]" : "items-center",
                  !prefix && "pl-2",
                  textColor,
                  error && "border-red-400",
                  borderClassNames.input,
                  disabled && "cursor-not-allowed",
                  !disabled && "cursor-text",
                )}
                data-loc={containerDataLoc}
                extensions={extensions}
                indentWithTab={false}
                placeholder={placeholder}
                readOnly={disabled || readOnly}
                theme={theme}
                value={value}
                onBlur={handleBlur}
                onChange={onChange}
                onCreateEditor={(view) => {
                  onCreateEditor(view);
                  dispatchCreateEvent();
                }}
                onFocus={handleFocus}
              >
                {wrappedChildren}
              </ReactCodeMirror>
            )}
          </div>
        </div>
      </Tooltip>
    );

    if (variant === "cell") {
      return inner;
    }

    return (
      <div
        ref={outerWrapperRef}
        className={twJoin(
          "relative h-8 flex-grow",
          borderClassNames.wrapper,
          expandable && "focus-within:z-[15]",
        )}
      >
        {inner}
      </div>
    );
  },
);
